import uiI18n from "../../i18n";
import { CATEGORY_KEYS } from "../../utils/propertyRequestForm";
import { vacationTypeKey, vacationTypeLabel } from "../../utils/vacationTypes";
import { cityLabel as placeCity, delegationLabel as placeDelegation } from "../../utils/placeLabels";

// Display helpers: business values stay canonical (e.g. "Sahloul"), only labels are translated.
// Place labels use the shared helpers in the current language (`t` is kept for the existing call sites).
// In "vacation", the same values carry the labels of the vacation publication form (entirePlace / oneRoom / sharedRoom).
export const categoryLabel = (t, category, transaction) =>
  transaction === "vacation" && vacationTypeKey(category) ? vacationTypeLabel(t, category) : t(`categories.${CATEGORY_KEYS[category]}`, category);
export const cityLabel = (t, city) => placeCity(city);
export const delegationLabel = (t, city, delegation) => placeDelegation(city, delegation);

const numbers = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 });
// Thousands are grouped with a no-break space (U+00A0): an ordinary space lets the RTL bidi algorithm
// reorder the groups ("200 000" shown as "000 200" in Arabic). Western digits are kept on every language.
export const formatNumber = (value) => numbers.format(Number(value)).replace(/[\s\u202F]/g, "\u00A0");
// In RTL, each value (number + unit) is a first-strong isolate (FSI ... PDI, the text equivalent of <bdi>),
// so "min – max" and the unit keep their order inside sentences and joined strings. LTR text is unchanged.
const isolate = (text) => (uiI18n.dir() === "rtl" ? `\u2068${text}\u2069` : text);

export function formatDate(value, language) {
  if (!value) return "";
  const locale = language === "en" ? "en-GB" : language === "fr" ? "fr-FR" : "ar-TN-u-nu-latn";
  return new Date(value).toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });
}

// "min X", "max Y", "X – Y" or "Non précisé".
export function rangeLabel(t, min, max, format) {
  const has = (value) => value !== null && value !== undefined && value !== "";
  if (has(min) && has(max)) return t("propertyRequest.summary.between", { min: format(min), max: format(max) });
  if (has(min)) return t("propertyRequest.summary.min", { value: format(min) });
  if (has(max)) return t("propertyRequest.summary.max", { value: format(max) });
  return t("propertyRequest.summary.none");
}

export const amount = (t, transaction) => (value) => isolate(`${formatNumber(value)} ${t(`propertyRequest.budgetUnit.${transaction || "sale"}`)}`);
export const area = (value) => isolate(`${formatNumber(value)} m²`);

export function placeLabel(t, city, delegations = []) {
  const parts = delegations.map((d) => delegationLabel(t, city, d));
  return [...parts, cityLabel(t, city)].filter(Boolean).join(uiI18n.dir() === "rtl" ? "، " : ", ");
}

export function requestTitle(t, request) {
  return t("propertyRequest.cardTitle", {
    type: (request.categories || []).map((c) => categoryLabel(t, c, request.transaction)).join(" / "),
    place: placeLabel(t, request.city, request.delegations || []),
  });
}
